// Vendors.
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import Chip from "@mui/material/Chip";

// Custom.
import Layout from "containers/Layout/Layout";
import {
  GET_USERS,
  GET_USERS_SIMPLIFIED,
  DELETE_USER,
} from "@vocollege/app/dist/modules/VoApi/graphql";
import { gql } from "@apollo/client";
import VoApi from "@vocollege/app/dist/modules/VoApi";
import I18n from "@vocollege/app/dist/modules/Services/I18n";
import EnhancedTable, {
  EnhancedTableRoles,
  EnhancedTableUserActive,
  EnhancedTableFilterValue,
} from "@vocollege/components/dist/EnhancedTable";

import DownloadExcelButton, {DownloadExcelButtonProps, Sheet} from "@vocollege/components/dist/DownloadExcelButton";
import { VoAppContext } from "services/GlobalState";
import { GeneralObject } from "@vocollege/app/dist/global";

const GET_USERS_EXCEL = gql`
  query Users(
    $search: String
    $orderBy: [GetUsersOrderByClause]
    $filters: [GetUsersFilter]
    $returnAll: Boolean
  ) {
    users(
      search: $search
      orderBy: $orderBy
      filters: $filters
      returnAll: $returnAll
    ) {
      data {
        id
        name
        email
        active
        status
        updated_at
        created_at
        roles {
          label
        }
        validig {
          id
        }
      }
    }
  }
`;

const excludeRow = (key: String) => {
  if (!key || key.startsWith('_')) return true;
  return false;
};

const formatExcelUserData = (data: any): Sheet[] | null =>  {
  if (!data?.users?.data && data.users.data.length > 0) return null;
  
  const headers = Object.keys(data.users.data[0])
    .filter((key: string) => !excludeRow(key))
    .map(key => String(key));

  const rows: any[] = [headers];

  data.users.data.forEach((user: any) => {
    const row: any[] = [];
    Object.entries(user).forEach(([key, value]) => {
      if (excludeRow(key)) return;

      switch (key) {
        case 'roles': 
            let val = String(value);
            if (Array.isArray(value)) {
              val = value.map((role) => role.label).join(' ');
            }
            row.push(val);
          break;
        case 'validig':
          row.push(value ? "true" :"false");
          break;
        default:
          row.push(String(value)); 
      }
    });
    rows.push(row);
  });

  
  return [{name: "Users", rows}];
};

const Users: React.FC = () => {
  const { state } = useContext(VoAppContext);
  const [roles, setRoles] = useState<EnhancedTableFilterValue[]>([]);
  const [groups, setGroups] = useState<EnhancedTableFilterValue[]>([]);
  let navigate = useNavigate();

  const editItem = (id = "create") => {
    navigate(`/users/${id}`);
  };

  const getRoles = (data: any) => {
    if (state.group) {
      return data.groupRoles;
    }
    return data.roles;
  };

  const getFilters = () => {
    return [
      {
        name: "active",
        label: I18n.get.misc.active,
        default: ["all"],
        values: [
          { label: I18n.get.actions.showAll, value: "all" },
          { label: I18n.get.misc.active, value: "true" },
          { label: I18n.get.misc.inactive, value: "false" },
        ],
      },
      {
        name: "roles",
        label: I18n.get.misc.roles,
        default: ["all"],
        multiple: true,
        values: roles,
      },
      {
        name: "groups",
        label: I18n.get.group.labels.groups,
        default: ["all"],
        multiple: true,
        values: groups,
      },
    ];
  };

  const handleDataChange = (data: any) => {
    if (state.group) {
      return;
    }
    setRoles([
      { label: I18n.get.actions.showAll, value: "all" },
      ...getRoles(data).map((v: GeneralObject) => ({
        label: v.label,
        value: v.id,
      })),
    ]);
    setGroups([
      { label: I18n.get.actions.showAll, value: "all" },
      ...data.allGroups.map((v: GeneralObject) => ({
        label: v.title,
        value: v.id,
      })),
    ]);
  };

  return (
    <Layout>
      {state.group && (
        <Alert sx={{ marginTop: 3 }} severity="warning">
          {I18n.get.group.messages.userComesFromCentralDatabase}
        </Alert>
      )}
      <EnhancedTable
        className="vo-global__top-bottom-space"
        enableSearch={!state.group}
        title={I18n.get.misc.user}
        operations={{
          category: "users",
          get: state.group ? GET_USERS_SIMPLIFIED : GET_USERS,
          delete: DELETE_USER,
        }}
        columns={[
          { title: "ID", field: "id" },
          {
            title: I18n.get.misc.active,
            field: "active",
            width: 240,
            render: (data: any) => (
              <EnhancedTableUserActive data={data.active} />
            ),
          },
          ...(state.group
            ? []
            : [
                {
                  title: I18n.get.misc.validig,
                  field: "validig",
                  render: (data: any) =>
                    data.validig ? (
                      <Chip
                        label={I18n.get.actions.yes}
                        size="small"
                        color="info"
                      />
                    ) : (
                      I18n.get.actions.no
                    ),
                },
              ]),
          { title: I18n.get.user.labels.name, field: "name" },
          { title: I18n.get.user.labels.email, field: "email" },
          {
            title: I18n.get.misc.roles,
            field: "roles",
            width: 240,
            render: (data: any) => <EnhancedTableRoles data={getRoles(data)} />,
          },
          ...(state.group
            ? []
            : [
                {
                  title: I18n.get.misc.groups,
                  field: "groups",
                  render: (data: any) => (
                    <EnhancedTableRoles data={data.groups} />
                  ),
                },
              ]),
          {
            title: I18n.get.misc.created_at,
            field: "created_at",
            width: 200,
          },
          {
            title: I18n.get.misc.updated_at,
            field: "updated_at",
            width: 200,
          },
        ]}
        addItem={editItem}
        editItem={editItem}
        orderBy="UPDATED_AT"
        order="DESC"
        filters={!state.group ? getFilters() : undefined}
        onDataChange={handleDataChange}
        excelQuery={GET_USERS_EXCEL}
        excelQueryProps={{returnAll: true }}
        excelDownloadFilename={"Users"}
        handleExcelQueryData={formatExcelUserData}
      />
    </Layout>
  );
};

export default Users;
